import React from 'react';

import {
  ArnmRepairsGrid,
  ArnmRepairsFilter,
  PermissionsGate,
  AddArnmRepairsModal,
} from '@components';
import {
  KanbanBoard,
  GridToolbar,
  KanbanWrapper,
  TablePagination,
  KanbanCardInfoMenu,
} from 'components/common/grid';
import {
  useColumnVisibility,
  useKanbanVisibility,
  useViewportGridHeight,
  useDataGridModel,
  useKanbanCardInfo,
} from '@hooks';
import { ColumnIcon, ListIcon } from 'components/common/ui/Vectors';
import {
  generateSearchPlaceholder,
  getColumnsVisibility,
  getUrl,
} from '@utils';
import { useRouter } from 'next/router';
import { URLS } from 'layouts/DrawerLayout/constants';
import { Box } from '@mui/material';
import { ARM_REPAIRS_PAGE_SIZE } from 'hooks/arnmRepairs/useArnmRepairsOperations';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import ExportButton from 'components/common/ui/ExportButton/ExportButton';
import { ARNM_REPAIRS_FIELDS_TO_SEARCH } from 'hooks/arnmRepairs/constants';
import { FiltersModalV2 } from 'components/common/modals';
import { useArnmRepairsFilterConfig } from 'hooks/arnmRepairs';
import { ARNM_REPAIRS_TASK_STATUSES_NAMES } from '../constants';
import { repairFields } from '../ArnmRepairsFilter/arnmRepairsFilter.fields.data';
import { REPAIRS_KANBAN_CARD_INFO } from './kanbanCardInfo';

export const VIEW_MODES = {
  list: { value: 'list', icon: <ListIcon /> },
  kanban: { value: 'kanban', icon: <ColumnIcon /> },
};

const initialGridColumns = [
  { value: true, label: 'ID', key: 'id' },
  { value: true, label: 'Request Date', key: 'createdAt' },
  { value: true, label: 'Repair Stage', key: 'status' },
  { value: true, label: 'Service Point', key: 'servicePoint' },
  { value: true, label: 'Reason', key: 'reason' },
  { value: true, label: 'Date to Service Point', key: 'dateToServicePoint' },
  {
    value: true,
    label: 'Out Date from Service Point',
    key: 'outDateFromServicePoint',
  },
];

const ArnmRepaisContent = ({
  loading,
  arnmRepairs,
  page,
  total,
  columns,
  pageSize,
  servicePoints,
  filters,
  apiRef,
  assigneesWithNone,
  enums,
  arnmRepairsDataGridState,
  onGetArnmRepairs,
  onFiltersModalChange,
  onChangeRules,
  onSaveSnapshot,
  onExportRequest,
}) => {
  const router = useRouter();
  const viewMode = router?.query?.view || VIEW_MODES.kanban.value;
  const stages = Object.keys(ARNM_REPAIRS_TASK_STATUSES_NAMES).reduce(
    (acc, cur) => [
      ...acc,
      { text: ARNM_REPAIRS_TASK_STATUSES_NAMES[cur].label, value: cur },
    ],
    []
  );
  const columnVisibilityModel =
    arnmRepairsDataGridState?.columns?.columnVisibilityModel || {};
  const columnsLabels = getColumnsVisibility(
    columnVisibilityModel,
    initialGridColumns
  );

  const filterConfig = useArnmRepairsFilterConfig({
    servicePoints,
    assignees: assigneesWithNone,
    stages,
    viewMode,
  });
  const { handleChangeVisibility, gridColumns } = useColumnVisibility(
    apiRef,
    columnsLabels,
    loading
  );
  const {
    handleKanbanVisibility,
    kanbanColumns,
    kanbanFilteredColumns,
    kanbanHiddenColumns,
  } = useKanbanVisibility(columns);

  const { handlePageChange } = useDataGridModel({
    page,
    pageSize,
    defaultPageSize: ARM_REPAIRS_PAGE_SIZE,
    url: URLS.arnmRepairs,
  });

  const { gridHeight } = useViewportGridHeight({ customOffset: 270 });

  const { cardFields, handleToggleCardField } = useKanbanCardInfo(
    REPAIRS_KANBAN_CARD_INFO
  );

  const handleChangeColumnVisibility = (column) => {
    if (viewMode === VIEW_MODES.list.value) {
      handleChangeVisibility(column);
    } else {
      handleKanbanVisibility(column);
    }
    onSaveSnapshot();
  };

  const handleChangeView = (newView) => {
    onSaveSnapshot();

    router.push({
      pathname: URLS.arnmRepairs,
      query: { ...router.query, view: newView },
    });
  };

  const handleSubmitRules = (rules) => {
    router.push(getUrl({ ...router.query, page: '1' }, URLS.arnmRepairs));
    onChangeRules(rules);
  };

  return (
    <>
      {arnmRepairsDataGridState && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <ArnmRepairsFilter
            servicePoints={servicePoints}
            viewMode={viewMode}
            hasFilters={filterConfig.hasFilters}
            assignees={assigneesWithNone || []}
            stages={stages}
            onSubmit={onGetArnmRepairs}
          />
        </Box>
      )}
      {viewMode === VIEW_MODES.list.value && (
        <ArnmRepairsGrid
          apiRef={apiRef}
          hasFilters={filterConfig.hasFilters}
          loading={loading || !arnmRepairsDataGridState}
          arnmRepairs={arnmRepairs}
          initialState={arnmRepairsDataGridState}
          servicePoints={servicePoints}
          page={page}
          total={total}
          pageSize={pageSize}
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              filterConfig,
              gridColumns,
              initialGridColumns,
              viewModes: VIEW_MODES,
              selectedViewMode: viewMode,
              searchPlaceholder: generateSearchPlaceholder(
                enums?.searchableFields || ARNM_REPAIRS_FIELDS_TO_SEARCH
              ),
              extraToolbarItems: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <PermissionsGate entity={ENTITIES_PERMISIONS.WriteRepairs}>
                    <ExportButton
                      onExportRequest={onExportRequest}
                      isIconButton
                    />
                  </PermissionsGate>

                  <FiltersModalV2
                    isGridAction
                    onSubmit={handleSubmitRules}
                    initialState={filters.modalConditions}
                    fields={repairFields}
                    onStateChange={onFiltersModalChange}
                  />
                </Box>
              ),
              addItemComponent: (
                <AddArnmRepairsModal
                  onGetArnmRepairs={onGetArnmRepairs}
                  servicePoints={servicePoints}
                />
              ),
              onRefresh: onGetArnmRepairs,
              onViewModeChange: handleChangeView,
              onChangeColumnVisibility: handleChangeVisibility,
            },
          }}
        />
      )}
      {viewMode === VIEW_MODES.kanban.value && (
        <KanbanWrapper>
          <GridToolbar
            filterConfig={filterConfig}
            gridColumns={kanbanColumns || []}
            initialGridColumns={columns || []}
            onChangeColumnVisibility={handleChangeColumnVisibility}
            selectedViewMode={viewMode}
            viewModes={VIEW_MODES}
            onRefresh={onGetArnmRepairs}
            onViewModeChange={handleChangeView}
            searchPlaceholder={generateSearchPlaceholder(
              enums?.searchableFields || ARNM_REPAIRS_FIELDS_TO_SEARCH
            )}
            extraToolbarItems={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <PermissionsGate entity={ENTITIES_PERMISIONS.WriteRepairs}>
                  <ExportButton
                    onExportRequest={onExportRequest}
                    isIconButton
                  />
                </PermissionsGate>

                <FiltersModalV2
                  isGridAction
                  onSubmit={handleSubmitRules}
                  initialState={filters.modalConditions}
                  fields={repairFields}
                  onStateChange={onFiltersModalChange}
                />

                <KanbanCardInfoMenu
                  items={cardFields || []}
                  initialItems={REPAIRS_KANBAN_CARD_INFO}
                  onToggle={handleToggleCardField}
                />
              </Box>
            }
            addItemComponent={
              <AddArnmRepairsModal
                onGetArnmRepairs={onGetArnmRepairs}
                servicePoints={servicePoints}
              />
            }
          />
          <Box sx={{ height: gridHeight }}>
            <KanbanBoard
              columns={kanbanFilteredColumns || []}
              hiddenColumns={kanbanHiddenColumns || []}
              cardFields={cardFields || []}
            />
          </Box>
          <TablePagination
            total={total}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => handlePageChange(newPage)}
            onPageSizeChange={(newSize) => handlePageChange(1, newSize)}
          />
        </KanbanWrapper>
      )}
    </>
  );
};

export default ArnmRepaisContent;
